import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, videos } from '@/mocks/videos';
import type { VideoItem } from '@/mocks/videos';

const PAGE_SIZE = 12;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';

  const [activeCategory, setActiveCategory] = useState(slug || 'dianshiju');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'rating'>('hot');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      setCurrentPage(1);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  const filteredVideos = useMemo(() => {
    let result: VideoItem[] = videos.filter((v) => v.category === activeCategory);

    if (searchQuery) {
      const lowerQ = searchQuery.toLowerCase();
      result = result.filter(
        (v) =>
          v.title.toLowerCase().includes(lowerQ) ||
          v.tags.some((t) => t.toLowerCase().includes(lowerQ)) ||
          v.actors.some((a) => a.toLowerCase().includes(lowerQ))
      );
    }

    switch (sortBy) {
      case 'new':
        result = [...result].sort((a, b) => b.year - a.year);
        break;
      case 'rating':
        result = [...result].sort((a, b) => b.rating - a.rating);
        break;
      case 'hot':
      default:
        result = [...result].sort((a, b) => b.views - a.views);
        break;
    }

    return result;
  }, [activeCategory, searchQuery, sortBy]);

  const totalPages = Math.ceil(filteredVideos.length / PAGE_SIZE);
  const pageVideos = filteredVideos.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleCategoryChange = (catSlug: string) => {
    setActiveCategory(catSlug);
    setCurrentPage(1);
    window.history.pushState({}, '', `/category/${catSlug}`);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (currentCategory) {
      document.title = `${currentCategory.name} - 麻花视频在线观看${currentCategory.name}`;
    }
  }, [currentCategory]);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="container-page">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: currentCategory?.name || '分类' },
            ]}
          />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              {currentCategory?.name}
            </h1>
            {searchQuery && (
              <p className="text-sm text-foreground-500 mt-2">
                搜索 &quot;{searchQuery}&quot; 的结果，共找到 {filteredVideos.length} 个相关内容
              </p>
            )}
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
            <div className="flex items-center bg-background-100 rounded-full px-1 py-1">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50 shadow-sm'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value as typeof sortBy);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 outline-none focus:border-primary-400 cursor-pointer"
              >
                <option value="hot">最热</option>
                <option value="new">最新</option>
                <option value="rating">评分最高</option>
              </select>
            </div>
          </div>

          {/* Video Grid */}
          {pageVideos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-10">
              {pageVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
                <i className="ri-search-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">暂无相关内容</p>
              <p className="text-sm text-foreground-400 mt-1">试试其他分类或搜索词</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 py-8">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  currentPage === 1
                    ? 'text-foreground-300 cursor-not-allowed'
                    : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    currentPage === page
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  currentPage === totalPages
                    ? 'text-foreground-300 cursor-not-allowed'
                    : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": `${currentCategory?.name || '分类'} - 麻花视频`,
        "description": `麻花视频${currentCategory?.name || '内容'}分类，提供海量${currentCategory?.name || '视频'}免费在线观看。`,
        "url": `https://www.mahuavideo.com/category/${activeCategory}`,
        "isPartOf": {
          "@type": "WebSite",
          "name": "麻花视频",
          "url": "https://www.mahuavideo.com/"
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.mahuavideo.com/" },
            { "@type": "ListItem", "position": 2, "name": currentCategory?.name || '分类' }
          ]
        }
      }) }} />
    </>
  );
}